import {
  Directive,
  ElementRef,
  Input,
  HostListener,
  Renderer2,
  OnInit,
  OnDestroy,
  Inject,
  Optional,
} from '@angular/core';
import { Subject } from 'rxjs';

import { TOOLTIP_CONFIG, TOOLTIP_DEFAULTS, TooltipPlacement, TooltipEvent } from './tooltip.config';
import { TooltipConfig } from './tooltip.config';
import { ElementInfo, TooltipState } from './tooltip.interfaces';
import { TooltipPositioner } from './tooltip.positioner';
import { TooltipRenderer } from './tooltip.renderer';
import { TooltipEventManager } from './tooltip.event-manager';
import { TooltipUtils } from './tooltip.utils';

@Directive({
  selector: '[TooltipMCDT]',
})
export class TooltipMCDTDirective implements OnInit, OnDestroy {
  // ✅ Public inputs
  @Input('TooltipMCDT') tooltipHtml: string = '';
  @Input() positionClass: TooltipPlacement = 'top';
  @Input() hideDelay?: number = TOOLTIP_DEFAULTS.HIDE_DELAY;

  // ✅ Private state - todo centralizado
  private readonly state: TooltipState = {
    visible: false,
    element: null,
    hideTimeout: null,
    cachedElementInfo: null,
    cacheValidUntil: 0,
  };

  private readonly destroy$ = new Subject<void>();
  private readonly isTouchDevice: boolean;
  private readonly config: TooltipConfig;
  private readonly tooltipRenderer: TooltipRenderer;
  private isRxJSHandlingEvents = false;
  private enableDebugLogs = false; // ✅ OPTIMIZACIÓN: Deshabilitado en producción para rendimiento
  private tooltipParent: HTMLElement | null = null; // ✅ OPTIMIZACIÓN: Cache del parent para cleanup

  constructor(
    private el: ElementRef,
    private renderer2: Renderer2,
    @Optional() @Inject(TOOLTIP_CONFIG) private injectedConfig?: Partial<TooltipConfig>,
  ) {
    this.isTouchDevice = TooltipUtils.detectTouchDevice();
    this.config = this.buildConfig(injectedConfig);
    this.tooltipRenderer = new TooltipRenderer(this.renderer2);
  }

  ngOnInit(): void {
    const validation = TooltipUtils.validateInputs(this.tooltipHtml, this.positionClass);
    if (!validation.isValid) return;

    if (validation.correctedPosition) {
      this.positionClass = validation.correctedPosition as TooltipPlacement;
    }

    this.setupDirective();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  // ✅ Event handlers
  private handleTooltipEvent = (eventType: TooltipEvent, event?: Event): void => {
    TooltipUtils.debugLog(
      `Event: ${eventType}, Visible: ${this.state.visible}, Touch: ${this.isTouchDevice}`,
      this.enableDebugLogs,
    );

    this.clearCurrentTimeout();

    switch (eventType) {
      case 'show':
        this.showTooltip();
        break;
      case 'hide':
        this.hideTooltip();
        break;
      case 'toggle':
        this.toggleTooltip();
        break;
    }
  };

  private handleDocumentClick = (event: Event): void => {
    if (!this.state.element) return;

    const target = event.target as HTMLElement;
    const isClickInside = this.el.nativeElement.contains(target) || this.state.element.contains(target);

    if (!isClickInside) {
      this.handleTooltipEvent('hide');
    }
  };

  // ✅ Core tooltip operations 
  private showTooltip(): void {
    if (this.state.visible || !this.tooltipHtml?.trim()) return;

    this.state.visible = true;
    this.createTooltip();
    TooltipUtils.debugLog('Tooltip shown', this.enableDebugLogs);
  }

  private hideTooltip(): void {
    if (!this.state.visible) return;

    this.state.visible = false;
    this.removeTooltipFromDOM();
    TooltipUtils.debugLog('Tooltip hidden', this.enableDebugLogs);
  }

  private toggleTooltip(): void {
    if (this.state.visible) {
      this.hideTooltip();
    } else {
      this.showTooltip();
      if (this.isTouchDevice && this.state.hideTimeout === null) {
        this.scheduleAutoHide();
      }
    }
  }

  // ✅ DOM operations - FIXED: Añadir siempre al body para posicionamiento fixed
  private createTooltip(): void {
    this.state.element = this.tooltipRenderer.createTooltip(this.tooltipHtml, this.config, this.el.nativeElement);
    
    // FIXED: Añadir siempre al body para que position:fixed funcione correctamente
    this.tooltipParent = document.body;
    this.renderer2.appendChild(document.body, this.state.element);

    requestAnimationFrame(() => this.positionTooltip());
  }

  private positionTooltip(): void {
    if (!this.state.element) return;

    const { elementInfo, newCacheValidUntil } = TooltipUtils.getElementInfo(
      this.el.nativeElement,
      this.state.cachedElementInfo,
      this.state.cacheValidUntil,
    );

    this.state.cachedElementInfo = elementInfo;
    this.state.cacheValidUntil = newCacheValidUntil;

    const viewport = TooltipUtils.getViewportInfo();
    const position = TooltipPositioner.calculateOptimalPosition(elementInfo, this.positionClass, this.config, viewport, this.el.nativeElement);

    // Debug específico para Safari (móvil y desktop)
    if (TooltipUtils.isSafari()) {
      TooltipUtils.debugLog('Safari Positioning Debug', this.enableDebugLogs, {
        elementRect: elementInfo.rect,
        calculatedPosition: position,
        viewport,
        positionClass: this.positionClass,
        isSafariMobile: TooltipUtils.isSafariMobile(),
        isSafariDesktop: TooltipUtils.isSafari() && !TooltipUtils.isSafariMobile()
      });
    }

    this.tooltipRenderer.updatePosition(this.state.element, position);
  }

  // ✅ Utility methods
  private setupDirective(): void {
    this.renderer2.addClass(this.el.nativeElement, 'custom-tooltip-mcdt');
    this.setupEvents();

    if (this.config.enableA11y) {
      this.renderer2.setAttribute(this.el.nativeElement, 'aria-describedby', '');
    }
  }

  private setupEvents(): void {
    try {
      const contentValidator = () => !!this.tooltipHtml?.trim();
      const isVisible = () => this.state.visible;
      const invalidateCache = () => {
        this.state.cachedElementInfo = null;
        this.state.cacheValidUntil = 0;
      };

      if (this.isTouchDevice) {
        TooltipEventManager.setupTouchEvents(
          this.el.nativeElement,
          this.destroy$,
          this.handleTooltipEvent,
          contentValidator,
        );
      } else {
        TooltipEventManager.setupMouseEvents(
          this.el.nativeElement,
          this.destroy$,
          this.handleTooltipEvent,
          contentValidator,
        );
      }

      TooltipEventManager.setupGlobalEvents(
        this.destroy$,
        this.handleTooltipEvent,
        this.handleDocumentClick,
        invalidateCache,
        isVisible,
        this.isTouchDevice
        // Removido callback de reposicionamiento - ahora siempre oculta en scroll
      );

      this.isRxJSHandlingEvents = true;
    } catch (error) {
      console.warn('TooltipMCDT: RxJS setup failed, using HostListener fallback', error);
      this.isRxJSHandlingEvents = false;
    }
  }

  private scheduleAutoHide(): void {
    const delay = this.hideDelay ?? this.config.hideDelay;
    this.state.hideTimeout = TooltipUtils.createTimeout(
      () => this.handleTooltipEvent('hide'),
      delay,
      this.state.hideTimeout,
    );
  }

  private clearCurrentTimeout(): void {
    TooltipUtils.clearTimeout(this.state.hideTimeout);
    this.state.hideTimeout = null;
  }

  private removeTooltipFromDOM(): void {
    if (this.state.element && this.tooltipParent) {
      try {
        // ✅ OPTIMIZACIÓN CRÍTICA: Remover del parent correcto, no siempre del body
        this.renderer2.removeChild(this.tooltipParent, this.state.element);
      } catch (e) {
        // Fallback silent - no spam console en producción
        if (this.enableDebugLogs) {
          console.warn('Tooltip removal failed:', e);
        }
      }
      this.state.element = null;
      this.tooltipParent = null;
    }
  }

  private cleanup(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearCurrentTimeout();
    this.removeTooltipFromDOM();
  }

  private buildConfig(injectedConfig?: Partial<TooltipConfig>): TooltipConfig {
    return {
      hideDelay: injectedConfig?.hideDelay ?? TOOLTIP_DEFAULTS.HIDE_DELAY,
      mobileWidth: injectedConfig?.mobileWidth ?? TOOLTIP_DEFAULTS.MOBILE_WIDTH,
      desktopWidth: injectedConfig?.desktopWidth ?? TOOLTIP_DEFAULTS.DESKTOP_WIDTH,
      viewportMargin: injectedConfig?.viewportMargin ?? TOOLTIP_DEFAULTS.VIEWPORT_MARGIN,
      arrowMargin: injectedConfig?.arrowMargin ?? TOOLTIP_DEFAULTS.ARROW_MARGIN,
      enableA11y: injectedConfig?.enableA11y ?? true,
    };
  }

  // ✅ HostListeners - solo como fallback
  @HostListener('mouseenter', ['$event'])
  onMouseEnter(event: MouseEvent) {
    if (!this.isTouchDevice && this.tooltipHtml && !this.isRxJSHandlingEvents) {
      this.handleTooltipEvent('show', event);
    }
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    if (!this.isTouchDevice && !this.isRxJSHandlingEvents) {
      this.handleTooltipEvent('hide');
    }
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent) {
    if (this.isTouchDevice && this.tooltipHtml && !this.isRxJSHandlingEvents) {
      event.preventDefault();
      this.handleTooltipEvent('toggle', event);
    }
  }
}
